/**
 * Head Position Calculations
 *
 * Functions for computing head/mouth position from body position
 */

import {Position} from "../../types/position";

/**
 * Default head height offset in meters
 * Average human head height above body origin
 */
const DEFAULT_HEAD_HEIGHT = 1.6;

/**
 * Compute estimated head/mouth position from body position
 * Body position is typically at feet/base - add head height offset
 *
 * @param bodyPosition Body position (typically feet/base)
 * @param headHeight Height offset to add (default: 1.6m)
 * @returns Estimated head position
 */
export function computeHeadPosition(
  bodyPosition: Position,
  headHeight: number = DEFAULT_HEAD_HEIGHT
): Position {
  return {
    x: bodyPosition.x,
    y: bodyPosition.y + headHeight,
    z: bodyPosition.z,
  };
}

/**
 * Parse body height string to meters
 * Handles various formats: "170", "170cm", "1.7m", etc.
 *
 * @param bodyHeightStr Body height string from participant data
 * @param fallback Fallback height in meters (default: 1.6)
 * @returns Height in meters
 */
export function parseBodyHeight(
  bodyHeightStr?: string,
  fallback: number = 1.6
): number {
  if (!bodyHeightStr) return fallback;

  const str = bodyHeightStr.toLowerCase().trim();

  // Try parsing as number with optional unit
  const numMatch = str.match(/^([\d.]+)\s*(cm|m)?$/);
  if (numMatch) {
    const value = parseFloat(numMatch[1]);
    const unit = numMatch[2];

    if (isNaN(value)) return fallback;

    if (unit === "m") {
      return value;
    } else if (unit === "cm" || value > 10) {
      // Assume centimeters if > 10 or explicitly cm
      return value / 100;
    } else {
      return value;
    }
  }

  return fallback;
}

/**
 * Get vector from listener to target position
 *
 * @param listenerPosition Listener position
 * @param targetPosition Target position
 * @returns Vector from listener to target
 */
export function getVectorFromListener(
  listenerPosition: Position,
  targetPosition: Position
): Position {
  return {
    x: targetPosition.x - listenerPosition.x,
    y: targetPosition.y - listenerPosition.y,
    z: targetPosition.z - listenerPosition.z,
  };
}
